import { v6 as uuidv6 } from 'uuid'
import { Loader, Error } from '../../common'
import {
  fetchNonSecureAncillaryMaterials,
  saveNonSecureAncillaryMaterials,
} from '../../../actions/csr'
import { LockUnlockComponent, BrowseItem } from '.'
import { PANEL_ORDER } from '../../../constants/OrderConstants'
import { sortColumnByKey } from '../../../utils/sort'

import styles from '../../../assets/style/scss/csr.scss'

const mapStateToProps = state => {
  const {
    user: {
      data: { selectedOrgId: orgId },
    },
    settingsEducationPeriod: { selectedEducationPeriod: educationPeriodCd },
  } = state
  return {
    educationPeriodCd,
    orgId,
  }
}

const NonSecureAncillaryMaterials = ({
  orgId,
  educationPeriodCd,
  subpanel,
  panel,
  updatePanel,
}) => {
  const [nonSecureMaterials, setNonSecureMaterials] = useState([])
  const [errorFetching, setErrorFetching] = useState(null)
  const [fetchingNonSecureMaterials, setFetchingNonSecureMaterials] = useState(true)
  const [selectedTestAdmin, setSelectedTestAdmin] = useState(null)
  const [availableTestAdmins, setAvailableTestAdmins] = useState([])

  const fetchMaterials = async () => {
    const { data = {}, error = null } = await fetchNonSecureAncillaryMaterials(
      orgId,
      educationPeriodCd
    )
    setErrorFetching(error)
    setNonSecureMaterials(data)
    const populatedTestAdmins = []
    PANEL_ORDER.forEach(({ testAdmin, displayName }) => {
      if (data?.items?.[testAdmin]?.length > 0) {
        populatedTestAdmins.push({ testAdmin, displayName })
      }
    })
    setAvailableTestAdmins(populatedTestAdmins)
    setFetchingNonSecureMaterials(false)
  }

  const filterResponseByTestAdmin = data => {
    const filteredData = data?.items?.[selectedTestAdmin] ?? []
    return { data: sortColumnByKey(filteredData, ['description', 'cbSku'], 'asc') }
  }

  const filterMaterialsByTestAdmin = () => {
    return filterResponseByTestAdmin(nonSecureMaterials)
  }

  useEffect(() => {
    if (orgId && educationPeriodCd) {
      fetchMaterials()
    }
  }, [orgId, educationPeriodCd])

  const saveData = async itemsToSave => {
    const response = await saveNonSecureAncillaryMaterials(itemsToSave, orgId, educationPeriodCd)
    if (response.data) {
      await setNonSecureMaterials(response.data)
      return filterResponseByTestAdmin(response.data)
    }

    return response
  }

  const updateSubPanel = (subpanel, title) => {
    if (subpanel === -1) {
      updatePanel(-1)
    } else {
      updatePanel(panel, title, subpanel)
    }
  }

  const selectTestAdmin = ({ testAdmin, displayName }) => {
    setSelectedTestAdmin(testAdmin)
    updateSubPanel(1, `${displayName} Testing Materials`)
  }

  if (errorFetching) {
    return (
      <div className={styles['error-wrapper']}>
        <Error message={errorFetching} title="Error retrieving Non-Secure Ancillary Materials" />
      </div>
    )
  }

  if (fetchingNonSecureMaterials) {
    return <Loader />
  }

  switch (subpanel) {
    case 0:
      return (
        <div className={styles['tool-body-inner']}>
          <p className={styles['tool-body-divider']}>
            Select the materials below that you wish to lock or unlock the ability to order.
          </p>
          {availableTestAdmins.length > 0 ? (
            availableTestAdmins.map(({ testAdmin, displayName }) => (
              <BrowseItem
                key={uuidv6()}
                title={`${displayName} Testing Materials`}
                action={() => selectTestAdmin({ testAdmin, displayName })}
              />
            ))
          ) : (
            <p>None available</p>
          )}
        </div>
      )
    case 1:
      return (
        <LockUnlockComponent
          compId="nonSecureAncillaryMaterials"
          introText="Select the materials below that you wish to lock or unlock the ability to order."
          emptyText="There are no non-secure ancillary materials to unlock."
          fnFetch={filterMaterialsByTestAdmin}
          fnSave={saveData}
          itemProps={{
            itemId: 'cbSku',
            itemDescription: 'description',
            itemOptionalAttrs: ['testWindow'],
          }}
        />
      )
    default:
      return null
  }
}

export default connect(mapStateToProps)(NonSecureAncillaryMaterials)
